/**
 * Overview tab's "this year" band: GET /reports/summary for the current
 * calendar year for the shop's effective report type. Renders server values as-is -- tax
 * splits may differ from the total by +-1 minor unit by design, never
 * recomputed here.
 */
import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { CurrencySummary, ReportsSummaryResponse, ShopType } from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { actionError } from '../../components/actionError.ts';
import { useDisplayCurrency } from '../../currency/CurrencyContext.tsx';
import { MONEY_NUMERIC_SX, palette } from '../../theme.ts';
import { payoutRatioCaption } from '../dashboard/dashboardHelpers.ts';
import { SHOP_TYPE_OPTIONS } from './shopsHelpers.ts';

export interface ShopFinancialsBandProps {
  shopId: number;
  shopType: ShopType;
}

type BandState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: ReportsSummaryResponse }
  | { kind: 'error'; message: string };

function StatementRow({
  label,
  detail,
  value,
  emphasize = false,
  divider = false,
}: Readonly<{
  label: string;
  detail?: string;
  value: string;
  emphasize?: boolean;
  divider?: boolean;
}>): React.JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        py: 1.25,
        borderTop: divider ? `1px solid ${palette.quietBorder}` : 'none',
      }}
    >
      <Box component="dt" sx={{ m: 0, minWidth: 0 }}>
        <Typography
          sx={{ color: emphasize ? palette.textPrimary : palette.textSecondary, fontWeight: emphasize ? 800 : 600 }}
        >
          {label}
        </Typography>
        {detail && (
          <Typography variant="caption" sx={{ color: palette.textSecondary }}>
            {detail}
          </Typography>
        )}
      </Box>
      <Typography
        component="dd"
        sx={{
          ...MONEY_NUMERIC_SX,
          m: 0,
          flexShrink: 0,
          textAlign: 'right',
          fontSize: emphasize ? '1.2rem' : '1rem',
          fontWeight: emphasize ? 800 : 700,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function FinancialStatement({
  summary,
  heading,
}: Readonly<{
  summary: CurrencySummary;
  heading?: string;
}>): React.JSX.Element {
  const { format } = useDisplayCurrency();
  const code = summary.currency_code;
  return (
    <Box>
      {heading && (
        <Typography variant="subtitle2" gutterBottom>
          {heading}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 } }}>
        <Box component="dl" data-testid="financial-statement" sx={{ m: 0, width: '100%', maxWidth: 620 }}>
          <StatementRow label="Gross sales" value={format(summary.gross_sales_minor, code)} />
          <StatementRow
            label="Less payout"
            value={format(summary.payout_minor, code)}
            detail={payoutRatioCaption(summary.payout_minor, summary.gross_sales_minor, summary.tax_total_minor)}
            divider
          />
          <StatementRow label="Gross profit" value={format(summary.gross_profit_minor, code)} emphasize divider />
          <StatementRow label="Less government tax" value={format(summary.tax_total_minor, code)} divider />
          <StatementRow label="Net profit" value={format(summary.profit_minor, code)} emphasize divider />
        </Box>

        <Box
          data-testid="ticket-levy"
          sx={{
            alignSelf: { xs: 'stretch', md: 'flex-start' },
            minWidth: { md: 240 },
            p: 2,
            border: `1px solid ${palette.quietBorder}`,
            borderRadius: 1,
          }}
        >
          <Typography sx={{ color: palette.textSecondary, fontWeight: 600 }}>Ticket levy uploaded</Typography>
          <Typography sx={{ ...MONEY_NUMERIC_SX, mt: 0.5, fontSize: '1.2rem', fontWeight: 800 }}>
            {format(summary.levy_minor, code)}
          </Typography>
          <Typography variant="caption" sx={{ color: palette.textSecondary }}>
            Reported separately
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function ShopFinancialsBand({ shopId, shopType }: Readonly<ShopFinancialsBandProps>): React.JSX.Element {
  const { api } = useAuth();
  const [state, setState] = useState<BandState>({ kind: 'loading' });
  const year = new Date().getFullYear();
  const typeLabel = SHOP_TYPE_OPTIONS.find((option) => option.value === shopType)?.label ?? shopType;
  // Guards an out-of-order response (rapid shopId change) the same way
  // ShopReportsSection's loaders do.
  const seqRef = useRef(0);

  useEffect(() => {
    const seq = ++seqRef.current;
    setState({ kind: 'loading' });
    api.reports
      .summary({ shop_id: shopId, year, report_type: shopType })
      .then((data) => {
        if (seqRef.current !== seq) return;
        setState({ kind: 'ready', data });
      })
      .catch((e: unknown) => {
        if (seqRef.current !== seq) return;
        setState({ kind: 'error', message: actionError(e) });
      });
  }, [api, shopId, shopType, year]);

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="overline" sx={{ color: palette.textSecondary }}>
        {year} financials - {typeLabel}
      </Typography>

      {state.kind === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
          <CircularProgress size={16} />
          <Typography sx={{ opacity: 0.7 }}>Loading...</Typography>
        </Box>
      )}

      {state.kind === 'error' && (
        <Alert role="alert" severity="error" sx={{ mt: 1 }}>
          {state.message}
        </Alert>
      )}

      {state.kind === 'ready' && state.data.by_currency.length === 0 && (
        <Typography sx={{ opacity: 0.75, mt: 1 }}>
          No {typeLabel.toLowerCase()} reports yet in {year}.
        </Typography>
      )}

      {state.kind === 'ready' && state.data.by_currency.length === 1 && (
        <Box sx={{ mt: 1 }}>
          <FinancialStatement summary={state.data.by_currency[0]!} />
        </Box>
      )}

      {state.kind === 'ready' && state.data.by_currency.length > 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Alert severity="warning">Reports span multiple currencies</Alert>
          {state.data.by_currency.map((c) => (
            <FinancialStatement key={c.currency_code} summary={c} heading={c.currency_code} />
          ))}
        </Box>
      )}
    </Paper>
  );
}

export default ShopFinancialsBand;
